import { trialExtensionModalOpenAtom } from '@/atoms/freeTrialModalAtoms';
import {
  clinicianMaxUpgradeModalEntryPointAtom,
  usePaywallStates,
} from '@/atoms/paywallAtoms';
import { getUsageLimitsHelpUrl } from '@/components/constants/billing-urls';
import { useOfflineGatedTooltip } from '@/components/offline-recording/use-offline-gated-tooltip';
import {
  useIsHeidi2FullUpgradeFlowEnabled,
  useIsHeidi2OptInEnabled,
  useIsHeidi2PracticeMaxUpgradeEnabled,
  useIsHeidi2TeamUpgradesEnabled,
  useIsHeidi2TrialEndEnabled,
  useIsRibbonSidebarEnabled,
  useIsSidebarIAEnabled,
} from '@/features/flags';
import { useQueryCreditAllowance } from '@/hooks/query/useQueryCreditAllowance';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import {
  trackSideNavCreditUsagePopoverCloseClick,
  trackSideNavCreditUsagePopoverExtendTrialClick,
  trackSideNavCreditUsagePopoverUpgradeClick,
  trackSideNavCreditUsagePopoverViewed,
} from '@/models/analytics/features/trackSideNavCreditUsagePopoverAnalytics';
import { trackSideNavCreditUsagePopoverTeamUpgradeRequestSubmit } from '@/models/analytics/features/trackTeamTrialUpgradeRequestAnalytics';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import {
  openTeamSeatUpgradeRequestModalAtom,
  pricingPlansModalOpenAtom,
  SideNavCreditUsagePopover,
  useIsPracticeMaxUpgradeAvailable,
  useIsTeamSeatUpgradeRequester,
  useShouldRenderSidebarCreditUsagePopover,
  type SideNavCreditUsagePopoverPorts,
} from '@heidi/commerce';

import {
  allPrimaryItemsCollapsedAtom,
  creditUsageCollapsedIntoPrimaryAtom,
  notificationsCollapsedIntoPrimaryAtom,
} from '@/atoms/sideNavAtoms';
import { useAtomValue, useSetAtom } from 'jotai';
import { CreditCard } from 'lucide-react';
import { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useIsRibbonRail } from './hooks/useIsRibbonRail';
import { useMobilePanel } from './mobile';
import {
  useFooterRailCollapsible,
  type FooterMoreRowDescriptor,
} from './sidebar/footer-rail/footer-rail-context';
import { useIsGamifyTrialExtensionEntryEnabled } from './use-is-gamify-trial-extension-entry-enabled';

type UpgradeActions = ReturnType<SideNavCreditUsagePopoverPorts['useUpgradeActions']>;

/**
 * The five modal openers the popover reaches today, resolved app-side. The
 * Clinician Max opener keeps the entry-point write that precedes its toggle.
 */
function useUpgradeActions(): UpgradeActions {
  const {
    toggleClinicianMaxUpgradeModal,
    toggleClinicianUpgradeModal,
    togglePracticeUpgradeModal,
  } = usePaywallStates();
  const setEntryPoint = useSetAtom(clinicianMaxUpgradeModalEntryPointAtom);
  const setTrialExtensionModalOpen = useSetAtom(trialExtensionModalOpenAtom);
  const setPricingPlansModalOpen = useSetAtom(pricingPlansModalOpenAtom);
  const openTeamSeatUpgradeRequestModal = useSetAtom(openTeamSeatUpgradeRequestModalAtom);
  const isTeamSeatUpgradeRequester = useIsTeamSeatUpgradeRequester();

  const openClinicianMaxUpgradeModal = useCallback(() => {
    setEntryPoint('side_nav_credit_usage_popover');
    toggleClinicianMaxUpgradeModal(true);
  }, [setEntryPoint, toggleClinicianMaxUpgradeModal]);
  const openClinicianUpgradeModal = useCallback(
    () => toggleClinicianUpgradeModal(true),
    [toggleClinicianUpgradeModal]
  );
  const openPracticeUpgradeModal = useCallback(
    () => togglePracticeUpgradeModal(true),
    [togglePracticeUpgradeModal]
  );
  const openPricingPlansModal = useCallback(() => {
    if (isTeamSeatUpgradeRequester) {
      openTeamSeatUpgradeRequestModal({
        trackRequestSubmitted: trackSideNavCreditUsagePopoverTeamUpgradeRequestSubmit,
      });
      return;
    }
    setPricingPlansModalOpen(true);
  }, [
    isTeamSeatUpgradeRequester,
    openTeamSeatUpgradeRequestModal,
    setPricingPlansModalOpen,
  ]);
  const openTrialExtensionModal = useCallback(
    () => setTrialExtensionModalOpen(true),
    [setTrialExtensionModalOpen]
  );

  return useMemo(
    () => ({
      openClinicianMaxUpgradeModal,
      openClinicianUpgradeModal,
      openPracticeUpgradeModal,
      openPricingPlansModal,
      openTrialExtensionModal,
    }),
    [
      openClinicianMaxUpgradeModal,
      openClinicianUpgradeModal,
      openPracticeUpgradeModal,
      openPricingPlansModal,
      openTrialExtensionModal,
    ]
  );
}

/**
 * Hooks cross BY REFERENCE, never as resolved values: the slot renders the
 * popover only for the cohort that sees it, and a value read here would run the
 * allowance query and its PostHog exposures for everyone else.
 */
const SIDE_NAV_CREDIT_USAGE_POPOVER_PORTS: SideNavCreditUsagePopoverPorts = {
  useIsHeidi2OptInEnabled,
  useIsHeidi2FullUpgradeFlowEnabled,
  useIsHeidi2TrialEndEnabled,
  useIsHeidi2TeamUpgradesEnabled,
  useIsRibbonSidebarEnabled,
  useMobilePanel,
  useIsGamifyTrialExtensionEntryEnabled,
  useOfflineGatedTooltip,
  useIsPracticeMaxUpgradeAvailable,
  useQueryCreditAllowance,
  useQueuedModal,
  useUpgradeActions,
  isAppleCompliantBuild,
  getUsageLimitsHelpUrl,
  trackSideNavCreditUsagePopoverViewed,
  trackSideNavCreditUsagePopoverUpgradeClick,
  trackSideNavCreditUsagePopoverExtendTrialClick,
  trackSideNavCreditUsagePopoverCloseClick,
};

/** Sidebar-footer credit usage tile. */
export function SideNavFooterUpgradeSlot() {
  const isHeidi2OptInEnabled = useIsHeidi2OptInEnabled();
  const isPracticeMaxUpgradeEnabled = useIsHeidi2PracticeMaxUpgradeEnabled();
  const isTeamUpgradesEnabled = useIsHeidi2TeamUpgradesEnabled();
  const showCreditUsagePopover = useShouldRenderSidebarCreditUsagePopover({
    isHeidi2OptInEnabled,
    isPracticeMaxUpgradeEnabled,
    isTeamUpgradesEnabled,
  });

  // Sidebar IA: the usage slot rides the footer rail budget. When the budget
  // collapses it, the slot hides and a "Usage" row surfaces in the primary
  // nav More instead of a footer More.
  const isSidebarIAEnabled = useIsSidebarIAEnabled();
  const isRibbonRail = useIsRibbonRail();
  const intl = useIntl();
  // The footer rail only starts collapsing after every primary nav fixed item
  // has folded into More (very small sidebar, sidebar IA).
  const allPrimaryCollapsed = useAtomValue(allPrimaryItemsCollapsedAtom);
  const hasContent = showCreditUsagePopover;
  const usageDescriptor = useMemo<FooterMoreRowDescriptor>(
    () => ({
      id: 'usage',
      label: intl.formatMessage({ defaultMessage: 'Usage', id: 'wbsq7OdxnV' }),
      icon: <CreditCard />,
      testId: 'sidebar-footer-credit-usage',
    }),
    [intl]
  );
  const isUsageCollapsed = useFooterRailCollapsible({
    id: 'usage',
    visible: isSidebarIAEnabled && allPrimaryCollapsed && isRibbonRail && hasContent,
    pinned: false,
    descriptor: usageDescriptor,
  });
  // Usage folds last — only after Notifications has committed its collapse.
  const notificationsCommitted = useAtomValue(notificationsCollapsedIntoPrimaryAtom);
  const setCreditUsageCollapsedIntoPrimary = useSetAtom(
    creditUsageCollapsedIntoPrimaryAtom
  );
  useEffect(() => {
    setCreditUsageCollapsedIntoPrimary(isUsageCollapsed && notificationsCommitted);
  }, [isUsageCollapsed, notificationsCommitted, setCreditUsageCollapsedIntoPrimary]);

  // Collapsing UNMOUNTS nothing here: the slot stays mounted (returning null)
  // so the `usage` rail registration above survives. Unmounting would run the
  // unregister cleanup, drop ~usage-height off the split's stack, re-fit the
  // remaining tiles, and latch them show/hide against the frozen budget — the
  // flicker. The primary nav More row replaces the tile via the atom.
  if (isUsageCollapsed && notificationsCommitted) {
    return null;
  }

  if (showCreditUsagePopover) {
    return <SideNavCreditUsagePopover ports={SIDE_NAV_CREDIT_USAGE_POPOVER_PORTS} />;
  }

  return null;
}
